import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Helper function to replace path params like :projectId, :kernelId, etc.
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key),
  );
}

// --- Submissions API types (new) ---
type Member = {
  userId: string;
  name: string;
  id: string; // student roll/id
  email: string;
};

type StudentSubmissionItem = {
  members: Member[];
  lastSubmissionDate: string; // ISO string
  lastSubmissionStatus: "submitted" | "graded" | "late" | string;
  lastSubmittedCheckpointName: string;
};

export type ProjectSubmissionsAPI = {
  isSuccess: boolean;
  message: string;
  data: {
    totalStudents: number;
    numberSubmitted: number;
    numberGraded: number;
    numberYetToGrade: number;
    studentSubmissions: StudentSubmissionItem[];
  };
};

// Type for individual submission details
type User = {
  id: string;
  name: string;
  email: string;
  profilePicture: string;
};

type Project = {
  id: string;
  title: string;
  dueDate: string;
  difficultyLevel: string;
  course: {
    id: string;
  };
};

type Grade = {
  id: string;
  totalScore: string;
  maxScore: string;
  percentage: string;
  letterGrade: string;
  feedback: string;
  rubricScores?: Record<string, any>;
  gradedAt: string;
};

type CheckpointProgress = {
  status: string;
  submittedAt: string | null;
  attempts: number;
  studentNotes: string | null;
  timeSpentMinutes: number;
  filesSubmitted: File[];
  returnedAt: string | null;
};

export type File = {
  fileName?: string;
  presignedUrl?:
    | string
    | {
        presignedUrl?: string;
      };
};

type UrlItem =
  | string
  | {
      fileName?: string;
      presignedUrl?: string | { presignedUrl?: string };
    };

type Checkpoint = {
  id: string;
  project_id: string;
  title: string;
  description: string;
  checkpoint_number: number;
  instructor_feedback: string;
  start_date: string;
  due_date: string;
  weight_percentage: string;
  is_required: boolean;
  status: string;
  created_by: string;
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  progress: CheckpointProgress;
};

type Submission = {
  id: string;
  status: string;
  submittedAt: string;
  executionTime: number | null;
  timeSpent: number;
  attempts: number;
  submissionSummary: any;
  metadata: Record<string, any>;
  user: User;
  project: Project;
  grade: Grade;
  createdAt: string;
  updatedAt: string;
  checkpointTitle?: string;
};

export type ProjectSubmissionsResponse = {
  isSuccess: boolean;
  message: string;
  data: {
    submission: {
      submission: Submission;
      checkpoints: Checkpoint[];
      notebookPresignedUrls: UrlItem[];
      filePresignedUrls: UrlItem[];
    };
  };
};

export async function getProjectSubmissions(
  projectId: string,
): Promise<ProjectSubmissionsAPI> {
  try {

    // Get the base URL with endpoint
    const url = getBITSEndApiMap("getProjectSubmissions").replace(
      ":projectId",
      projectId,
    );

    const { data } = await api.get<ProjectSubmissionsAPI>(url);

    return data;
  } catch (error) {
    console.error("Error fetching project submissions:", error);
    throw error;
  }
}

export async function getStudentSubmissionDetails(
  projectId: string,
  userId: string,
): Promise<ProjectSubmissionsResponse> {
  try {
    const url = getBITSEndApiMap("getProjectSubmissionDetails")
      .replace(":userId", userId)
      .replace(":projectId", projectId);

    const { data } = await api.get<ProjectSubmissionsResponse>(url);

    return data;
  } catch (error) {
    console.error("Error fetching student submission details:", error);
    throw error;
  }
}
